import store from '../../state/';
import { TacModel } from '../../state/tac/tacTypes';
import { Dialog } from '../../state/dialog/dialogTypes';
import { selectDialogOptions } from '../../state/dialog/dialogSelectors';
import { openAmeliorationDialog } from '../../state/dialog/dialogActions';
import { showAmeliorationToast } from '../toastMaster';
import {
  Amelioration,
  AmeliorationArgs,
  AmeliorationCache,
  AmeliorationDialogCallbacks,
  AmeliorationOutcome,
  AmeliorationResult,
  TransformationResult,
} from './ameliorationTypes';
import _ from 'lodash';

export const ameliorationDialogCallbacks: AmeliorationDialogCallbacks = {};
let busy: boolean = false;

export const setupAmelioration = (args: AmeliorationArgs): Amelioration => {
  const cache: AmeliorationCache = {
    tacIn: null,
    canAmeliorate: null,
    tacOut: null,
    successMessage: null,
  };

  const clearCacheIfNeeded = (tacIn: TacModel | undefined): boolean => {
    if (tacIn && JSON.stringify(tacIn) === JSON.stringify(cache.tacIn))
      return false;

    cache.tacIn = tacIn ? _.cloneDeep(tacIn) : null;
    cache.canAmeliorate = null;
    cache.tacOut = null;
    cache.successMessage = null;

    return true;
  };

  let userResponsePromise: any;
  let userHasAccepted: any;
  const setupUserResponsePromise = () => {
    userResponsePromise = new Promise((resolve, reject) => {
      userHasAccepted = resolve;
    });
  };

  const onAcceptClicked = (): void => {
    cache.successMessage && showAmeliorationToast(cache.successMessage);
    userHasAccepted(true);
  };

  const onDeclineClicked = (): void => {
    userHasAccepted(false);
  };

  const transformAndSetCache = (tacIn: TacModel): void => {
    const transformationResult: TransformationResult = args.transform(tacIn);
    cache.canAmeliorate = transformationResult.success;
    if (transformationResult.success) {
      cache.tacOut = _.cloneDeep(transformationResult.tacOut);
      cache.successMessage = transformationResult.successMessage;
    }
  };

  const checkWhetherCanAmeliorate = (tacIn: TacModel): boolean | null => {
    if (busy) return null;

    clearCacheIfNeeded(tacIn);

    if (cache.canAmeliorate !== null) return cache.canAmeliorate;

    if (args.fastCheck) {
      const canAmeliorate = args.fastCheck(tacIn);
      cache.canAmeliorate = canAmeliorate;
      return canAmeliorate;
    }

    transformAndSetCache(tacIn);

    return !!cache.canAmeliorate;
  };

  const start = async (tacIn: TacModel): Promise<AmeliorationResult> => {
    if (busy) {
      const busyAmeliorationResult: AmeliorationResult = {
        outcome: AmeliorationOutcome.Busy,
      };

      return busyAmeliorationResult;
    }

    clearCacheIfNeeded(tacIn);

    if (!(cache.canAmeliorate === false) && !cache.tacOut) {
      transformAndSetCache(tacIn);
    }

    if (!cache.canAmeliorate) {
      const failureAmeliorationResult: AmeliorationResult = {
        outcome: AmeliorationOutcome.Failure,
      };

      return failureAmeliorationResult;
    }

    ameliorationDialogCallbacks.onAcceptClicked = onAcceptClicked;
    ameliorationDialogCallbacks.onDeclineClicked = onDeclineClicked;

    let options: Dialog = selectDialogOptions(store.getState());
    if (!options) options = {};
    if (!options.options) options.options = {};
    if (!options.options.ameliorationData)
      options.options.ameliorationData = {};
    const { ameliorationData } = options.options;
    ameliorationData.dialogContent = args.dialogContent;

    busy = true;
    setupUserResponsePromise();
    store.dispatch(openAmeliorationDialog(options));
    const acceptedByUser = await userResponsePromise;
    busy = false;

    if (acceptedByUser) {
      const successAmeliorationResult: AmeliorationResult = {
        outcome: AmeliorationOutcome.Success,
        tacOut: _.cloneDeep(cache.tacOut) || undefined,
        successMessage: cache.successMessage || undefined,
      };

      return successAmeliorationResult;
    }

    // If we get here, the amelioration has been declined by the user.
    const declinedAmeliorationResult: AmeliorationResult = {
      outcome: AmeliorationOutcome.Declined,
    };

    return declinedAmeliorationResult;
  };

  const amelioration: Amelioration = {
    start,
    checkWhetherCanAmeliorate,
  };

  return amelioration;
};
